import { useTranslation } from "@refinedev/core";

import { MenuItem, Select, type SelectChangeEvent, Stack, Typography } from "@mui/material";

import { AlertEmailPreference } from "@/hooks/useAlertEmailPreference";

import {
  CoverageDialogShell,
  type CoveragePractitionerOption,
  DEFAULT_COVERAGE_PREFIX,
  EmailPreferenceField,
  FieldLabel,
} from "./coverage-dialog-parts";

export interface AddCoverageDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Translation key prefix containing the confirmAdd keys. */
  translationPrefix?: string;
  /** Practitioners that can be assigned as coverage. */
  practitionerOptions: CoveragePractitionerOption[];
  /** Currently selected coverage practitioner id (controlled). */
  selectedPractitionerId: string;
  onSelectedPractitionerChange: (id: string) => void;
  /** Alert email preference chosen in the dialog (controlled). */
  emailPreference: AlertEmailPreference;
  onEmailPreferenceChange: (preference: AlertEmailPreference) => void;
  /**
   * Name of the covered practitioner — injected into the org-admin copy via
   * `{{coveredName}}`. Self-service strings use "your/you" and ignore it.
   */
  coveredPractitionerName?: string;
  /** Additional confirm-disable condition (e.g. an in-flight lookup). */
  confirmDisabled?: boolean;
  /** Disable controls while the change is being saved. */
  isSaving?: boolean;
}

export const AddCoverageDialog = ({
  open,
  onConfirm,
  onCancel,
  translationPrefix = DEFAULT_COVERAGE_PREFIX,
  practitionerOptions,
  selectedPractitionerId,
  onSelectedPractitionerChange,
  emailPreference,
  onEmailPreferenceChange,
  coveredPractitionerName,
  confirmDisabled = false,
  isSaving = false,
}: AddCoverageDialogProps) => {
  const { translate } = useTranslation();

  const prefix = `${translationPrefix}.confirmAdd`;
  // escapeValue:false lets React handle escaping so names with apostrophes render correctly.
  const copyParams = { coveredName: coveredPractitionerName ?? "", interpolation: { escapeValue: false } };

  const handlePractitionerChange = (event: SelectChangeEvent) => {
    onSelectedPractitionerChange(event.target.value);
  };

  // While options are still loading (empty) the pre-populated selected id can be
  // out-of-range for one render. Fall back to "" (the displayEmpty placeholder) so
  // MUI doesn't warn about an out-of-range Select value. The confirm gate still
  // reads selectedPractitionerId directly, so this only affects the rendered value.
  const selectValue = practitionerOptions.some(option => option.id === selectedPractitionerId)
    ? selectedPractitionerId
    : "";

  return (
    <CoverageDialogShell
      open={open}
      title={translate(`${prefix}.title`)}
      onCancel={onCancel}
      onConfirm={onConfirm}
      confirmLabel={translate(`${prefix}.confirm`)}
      confirmColor="primary"
      confirmDisabled={!selectedPractitionerId || confirmDisabled}
      isSaving={isSaving}
    >
      <Typography variant="body2" color="text.primary">
        {translate(`${prefix}.subtitle`, copyParams)}
      </Typography>

      <Stack spacing={0.5}>
        <FieldLabel text={translate(`${prefix}.practitionerLabel`)} required />
        <Select
          fullWidth
          size="small"
          value={selectValue}
          onChange={handlePractitionerChange}
          displayEmpty
          disabled={isSaving}
          data-testid="coverage-practitioner-select"
        >
          <MenuItem value="" disabled>
            <Typography variant="body2" fontStyle="italic" color="text.secondary" component="span">
              {translate("common.selectOption")}
            </Typography>
          </MenuItem>
          {practitionerOptions.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <EmailPreferenceField
        label={translate(`${prefix}.emailPrefLabel`)}
        description={translate(`${prefix}.emailPrefDescription`, copyParams)}
        value={emailPreference}
        onChange={onEmailPreferenceChange}
        disabled={isSaving}
      />

      <Typography variant="body2" color="text.primary">
        {translate(`${prefix}.note`, copyParams)}
      </Typography>
    </CoverageDialogShell>
  );
};
